import { ArrowPathIcon, ChevronRightIcon, PlusIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router';
import { useEdiPurchaseOrderList } from '@/api/hanly-v2/edi/edi';
import { Sheet } from '@/components/ui/sheet';
import { useShowPrices } from '@/contexts/auth';
import { formatYMDSlash } from '@/utils/date';

const RECENT_ORDER_COUNT = 3;

const optionClassName =
  'flex min-h-14 items-center gap-3 border border-neutral-200 bg-white px-4 py-2 duration-300 hover:bg-neutral-50';

interface NewOrderStartSheetProps {
  open: boolean;
  onClose: () => void;
}

/** 新規発注を「商品を選んで作成」するか「過去の注文から再注文」するかを選ぶ */
export const NewOrderStartSheet = ({ open, onClose }: NewOrderStartSheetProps) => {
  const showPrices = useShowPrices();
  const { data, isLoading } = useEdiPurchaseOrderList(
    { page: 1, page_size: RECENT_ORDER_COUNT, ordering: '-created_at' },
    { query: { enabled: open } },
  );
  const recentOrders = data?.results ?? [];

  return (
    <Sheet open={open} onClose={onClose} title="新規発注">
      <div className="flex flex-col gap-6">
        <Link to="/orders/new" className={optionClassName}>
          <PlusIcon className="size-6 shrink-0 text-sky-800" />
          <span className="flex-1 font-bold">商品を選んで作成</span>
          <ChevronRightIcon className="size-5 text-neutral-400" />
        </Link>

        <section className="flex flex-col gap-2">
          <h4 className="font-bold">過去の注文から再注文</h4>
          {isLoading && <p className="text-sm text-neutral-600">読み込み中...</p>}
          {!isLoading && recentOrders.length === 0 && (
            <p className="text-sm text-neutral-600">過去の注文はありません</p>
          )}
          {recentOrders.map((order) => (
            <Link key={order.id} to={`/orders/new?from=${order.id}`} className={optionClassName}>
              <ArrowPathIcon className="size-6 shrink-0 text-neutral-600" />
              <span className="flex flex-1 flex-col leading-snug">
                <span className="font-bold">{formatYMDSlash(order.created_at)} の注文</span>
                <span className="text-sm text-neutral-600">
                  EDI No. {order.id}
                  {showPrices && ` ・ ¥${Number(order.total_amount).toLocaleString()}`}
                </span>
              </span>
              <ChevronRightIcon className="size-5 text-neutral-400" />
            </Link>
          ))}
          <p className="text-sm text-neutral-600">
            それより前の注文は、発注一覧で確定済の注文を開いて「この内容で再注文」を選んでください。
          </p>
        </section>
      </div>
    </Sheet>
  );
};
